import React from 'react'
import PageHeader from '../../componentsII/PageHeader';
import api from '../../axios/api';
import { TableContainer, TableBody, TableCell, TableFooter, TableHead, TablePagination, TableRow, Table, Paper, Switch, IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { useEffect, useState } from 'react';
import { X, Trash2 as DeleteIcon, PenBoxIcon, PlusIcon } from 'lucide-react';
import { format } from "date-fns";
import CustomButton from '../../componentsII/Buttons';
import { useDispatch } from 'react-redux';
import { showNotification } from '../../redux/slices/notificationSlice';
import AddUpdateVehicleEmissionFactorMaster from '../../components/Masters/EmissionVehicleDistanceMaster/AddUpdateVehicleEmissionFactorMaster';
import { TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import debounce from 'lodash/debounce';
import { Search } from 'lucide-react';

const EmissionVehicleDistanceWise = () => {
    const dispatch = useDispatch();
    const [emissionData, setEmissionData] = useState([]);
    const [isAddUpdateModalOpen, setIsAddUpdateModalOpen] = useState(false);
    const [selectedEmission, setSelectedEmission] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [emissionToDelete, setEmissionToDelete] = useState(null);

    const [totalEmissions, setTotalEmissions] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    
    const [filterOptions, setFilterOptions] = useState({
        vehicleTypes: [],
        vehicleSubTypes: [],
        fuels: [],
        years: []
    });

    const [filters, setFilters] = useState({
        search: '',
        status: 'ALL',
        vehicleTypeId: 'ALL',
        vehicleSubTypeId: 'ALL',
        fuelId: 'ALL',
        year: 'ALL',
        sortBy: 'created_date',
        sortOrder: 'desc'
    });

    const getEmissionData = async (pageNumber = page, limit = rowsPerPage, filterParams = filters) => {
        try {
            const response = await api.get('/admin/vehicleDistanceEmission', {
                params: {
                    page: pageNumber + 1,
                    limit,
                    search: filterParams.search,
                    status: filterParams.status === 'ALL' ? '' : filterParams.status,
                    vehicleTypeId: filterParams.vehicleTypeId === 'ALL' ? '' : filterParams.vehicleTypeId,
                    vehicleSubTypeId: filterParams.vehicleSubTypeId === 'ALL' ? '' : filterParams.vehicleSubTypeId,
                    fuelId: filterParams.fuelId === 'ALL' ? '' : filterParams.fuelId,
                    year: filterParams.year === 'ALL' ? '' : filterParams.year,
                    sortBy: filterParams.sortBy,
                    sortOrder: filterParams.sortOrder
                }
            });
            setEmissionData(response.data.data);
            setTotalEmissions(response.data.total);
        } catch (error) {
            dispatch(showNotification({
                message: 'Failed to fetch vehicle distance emission data',
                type: 'error',
            }));
        }
    }

    const getFilterOptions = async () => {
        try {
            const response = await api.get('/admin/vehicleDistanceEmissionFilterOptions');
            setFilterOptions(response.data);
        } catch (error) {
            console.error('Error fetching filter options:', error);
            dispatch(showNotification({
                message: 'Failed to fetch filter options',
                type: 'error',
            }));
        }
    }

    useEffect(() => {
        getEmissionData();
        getFilterOptions();
    }, [page, rowsPerPage, filters]);

    const debouncedSearch = debounce((value) => { 
        setFilters(prev => ({ ...prev, search: value })); 
        setPage(0); 
    }, 500);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleRemove = async (id) => {
        const emission = emissionData.find(item => item.id === id);
        setEmissionToDelete(emission);
        setDeleteDialogOpen(true);
    }

    const confirmDelete = async () => {
        try {
            setIsSubmitting(true);
            const response = await api.delete(`/admin/DeleteVehicleDistanceEmission/${emissionToDelete.id}`);
            if (response.data.status == 200) {
                dispatch(showNotification({
                    message: 'Vehicle emission data deleted successfully',
                    type: "success"
                }));
                getEmissionData();
            } else {
                dispatch(showNotification({
                    message: 'Failed to delete vehicle emission data',
                    type: "error"
                }));
            }
        } catch (error) {
            console.error('Error deleting vehicle emission data:', error);
            dispatch(showNotification({
                message: 'An error occurred while deleting',
                type: "error"
            }));
        } finally {
            setIsSubmitting(false);
            setDeleteDialogOpen(false);
            setEmissionToDelete(null);
        }
    }

    const handleEdit = async (emission) => {
        setSelectedEmission(emission);
        setIsAddUpdateModalOpen(true);
    }

    const handleActivateDeactivate = async (emission) => {
        try {
            setIsSubmitting(true);
            const response = await api.post(`/admin/activateDeactivateVehicleDistanceEmission/${emission.id}`, {
                is_active: emission.is_active == 1 ? 0 : 1
            });
            if (response.data.status == 200) {
                dispatch(showNotification({
                    message: emission.is_active ? 'Emission data deactivated successfully' : 'Emission data activated successfully',
                    type: "success"
                }));
                getEmissionData();
            } else {
                dispatch(showNotification({
                    message: emission.is_active ? 'Failed to deactivate emission data' : 'Failed to activate emission data',
                    type: "error"
                }));
            }
        } catch (error) {
            console.error('Error activating/deactivating emission data:', error);
            dispatch(showNotification({
                message: 'An error occurred',
                type: "error"
            }));
        } finally {
            setIsSubmitting(false);
        }
    }

    // Function to handle sorting
    const handleSort = (field) => {
        setFilters(prev => ({
            ...prev,
            sortBy: field,
            sortOrder: prev.sortBy === field && prev.sortOrder === 'asc' ? 'desc' : 'asc'
        }));
        setPage(0);
    };

    return (
        <>
            <PageHeader title="Vehicle Distance Wise Emission" className="px-5 py-5">
                <div className="flex flex-wrap justify-between gap-5 items-center mt-5">
                    <div className="flex flex-wrap items-center gap-4 flex-1">
                        {/* Search Field */}
                        <TextField
                            size="small"
                            placeholder="Search..."
                            InputProps={{
                                startAdornment: <Search className="w-5 h-5 text-gray-500 mr-2" />,
                            }}
                            onChange={(e) => debouncedSearch(e.target.value)}
                            className="min-w-[200px]"
                        />

                        {/* Vehicle Type Filter */}
                        <FormControl size="small" className="min-w-[150px]">
                            <InputLabel>Vehicle Type</InputLabel>
                            <Select
                                value={filters.vehicleTypeId}
                                label="Vehicle Type"
                                onChange={(e) => {
                                    setPage(0);
                                    setFilters(prev => ({ 
                                        ...prev, 
                                        vehicleTypeId: e.target.value,
                                        // Reset sub-type when type changes
                                        vehicleSubTypeId: 'ALL'
                                    }));
                                }}
                            >
                                <MenuItem value="ALL">All Types</MenuItem>
                                {filterOptions.vehicleTypes.map(type => (
                                    <MenuItem key={type.id} value={type.id}>{type.type_name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Vehicle Sub Type Filter */}
                        <FormControl size="small" className="min-w-[150px]">
                            <InputLabel>Sub Type</InputLabel>
                            <Select
                                value={filters.vehicleSubTypeId}
                                label="Sub Type"
                                onChange={(e) => {
                                    setPage(0);
                                    setFilters(prev => ({ ...prev, vehicleSubTypeId: e.target.value }));
                                }}
                            >
                                <MenuItem value="ALL">All Sub Types</MenuItem>
                                {filterOptions.vehicleSubTypes
                                    .filter(subType => 
                                        filters.vehicleTypeId === 'ALL' || 
                                        subType.vehicle_type_id == filters.vehicleTypeId
                                    )
                                    .map(subType => (
                                        <MenuItem key={subType.id} value={subType.id}>{subType.sub_type_name}</MenuItem>
                                    ))
                                }
                            </Select>
                        </FormControl>

                        {/* Fuel Filter */}
                        <FormControl size="small" className="min-w-[150px]">
                            <InputLabel>Fuel</InputLabel>
                            <Select
                                value={filters.fuelId}
                                label="Fuel"
                                onChange={(e) => {
                                    setPage(0);
                                    setFilters(prev => ({ ...prev, fuelId: e.target.value }));
                                }}
                            >
                                <MenuItem value="ALL">All Fuels</MenuItem>
                                {filterOptions.fuels.map(fuel => (
                                    <MenuItem key={fuel.id} value={fuel.id}>{fuel.fuel_name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Year Filter */}
                        <FormControl size="small" className="min-w-[120px]">
                            <InputLabel>Year</InputLabel>
                            <Select
                                value={filters.year}
                                label="Year"
                                onChange={(e) => {
                                    setPage(0);
                                    setFilters(prev => ({ ...prev, year: e.target.value }));
                                }}
                            >
                                <MenuItem value="ALL">All Years</MenuItem>
                                {filterOptions.years.map(year => (
                                    <MenuItem key={year} value={year}>{year}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Status Filter */}
                        <FormControl size="small" className="min-w-[120px]">
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={filters.status}
                                label="Status"
                                onChange={(e) => {
                                    setPage(0);
                                    setFilters(prev => ({ ...prev, status: e.target.value }));
                                }}
                            >
                                <MenuItem value="ALL">All</MenuItem>
                                <MenuItem value="ACTIVE">Active</MenuItem>
                                <MenuItem value="INACTIVE">Inactive</MenuItem>
                            </Select>
                        </FormControl>
                    </div>
                    
                    <CustomButton
                        title="Add Distance Wise Emission"
                        className="bg-green-600 hover:bg-green-700 text-white"
                        text={<><PlusIcon /> Add Distance Wise Emission</>}
                        click={() => {
                            setSelectedEmission(null);
                            setIsAddUpdateModalOpen(true);
                        }}
                    />
                </div>
                
                <TableContainer component={Paper} className="mt-4">
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell 
                                    onClick={() => handleSort('fuel_name')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    Fuel {filters.sortBy === 'fuel_name' && (filters.sortOrder === 'asc' ? '↑' : '↓')}
                                </TableCell>
                                <TableCell >
                                    Unit Name 
                                </TableCell>
                                <TableCell 
                                    onClick={() => handleSort('vehicle_type_name')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    Vehicle Type {filters.sortBy === 'vehicle_type_name' && (filters.sortOrder === 'asc' ? '↑' : '↓')}
                                </TableCell>
                                <TableCell 
                                    onClick={() => handleSort('vehicle_sub_type_name')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    Vehicle Sub Type {filters.sortBy === 'vehicle_sub_type_name' && (filters.sortOrder === 'asc' ? '↑' : '↓')}
                                </TableCell>
                                <TableCell 
                                    onClick={() => handleSort('valid_year')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    Year {filters.sortBy === 'valid_year' && (filters.sortOrder === 'asc' ? '↑' : '↓')}
                                </TableCell>
                                <TableCell 
                                    onClick={() => handleSort('emission_kgco2e')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    Emission kgCO2e {filters.sortBy === 'emission_kgco2e' && (filters.sortOrder === 'asc' ? '↑' : '↓')}
                                </TableCell>
                                <TableCell 
                                    onClick={() => handleSort('created_date')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    Created Date {filters.sortBy === 'created_date' && (filters.sortOrder === 'asc' ? '↑' : '↓')}
                                </TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {emissionData?.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">No records found</TableCell>
                                </TableRow>
                            ) : (
                                emissionData?.map((emission) => (
                                    <TableRow key={emission.id}>
                                        <TableCell>{emission.fuel_name || '-'}</TableCell>
                                        <TableCell>{emission.unit_name}</TableCell>
                                        <TableCell>{emission.vehicle_type_name || '-'}</TableCell>
                                        <TableCell>{emission.vehicle_sub_type_name || '-'}</TableCell>
                                        <TableCell>{emission.valid_year || '-'}</TableCell>
                                        <TableCell>{emission.emission_kgco2e || '-'}</TableCell>
                                        <TableCell>{emission.created_date ? format(new Date(emission.created_date), "dd-MM-yyyy") : "-"}</TableCell>
                                        <TableCell>
                                            <Switch
                                                checked={emission.is_active == "0" ? false : emission.is_active == "1" ? true : false}
                                                color="success"
                                                onChange={() => handleActivateDeactivate(emission)}
                                                disabled={isSubmitting}
                                            />
                                        </TableCell>

                                        <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleEdit(emission)}
                                                sx={{
                                                    backgroundColor: '#16A34A',
                                                    color: 'white',
                                                    '&:hover': { backgroundColor: '#00bf46' },
                                                    p: '8px',
                                                }}
                                            >
                                                <PenBoxIcon size={20} />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleRemove(emission.id)}
                                                sx={{
                                                    backgroundColor: '#df3e3e',
                                                    color: 'white',
                                                    '&:hover': { backgroundColor: '#ff0000' },
                                                    p: '8px',
                                                }}
                                            >
                                                <DeleteIcon size={20} />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                        <TableFooter>
                            <TableRow>
                                <TablePagination
                                    rowsPerPageOptions={[5, 10, 25, 50]}
                                    colSpan={8}
                                    count={totalEmissions}
                                    rowsPerPage={rowsPerPage}
                                    page={page}
                                    onPageChange={handleChangePage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                    labelDisplayedRows={({ from, to, count }) =>
                                        `Page ${page + 1} of ${Math.ceil(count / rowsPerPage)} | ${from}-${to} of ${count}`
                                    }
                                />
                            </TableRow>
                        </TableFooter>
                    </Table>
                </TableContainer>

                <Dialog
                    open={isAddUpdateModalOpen}
                    onClose={() => { setIsAddUpdateModalOpen(false); setSelectedEmission(null) }}
                    maxWidth="sm"
                    fullWidth
                >
                    <div className="flex justify-between items-start pt-2 px-2">
                        <DialogTitle className="text-lg font-bold mt-1">
                            {selectedEmission ? "Edit Vehicle Distance Emission" : "Add Vehicle Distance Emission"}
                        </DialogTitle>
                        <X
                            className="w-6 h-6 cursor-pointer text-gray-500 hover:text-gray-700 transition"
                            onClick={() => { setIsAddUpdateModalOpen(false); setSelectedEmission(null) }}
                        />
                    </div>
                    <DialogContent>
                        <AddUpdateVehicleEmissionFactorMaster
                            refetch={getEmissionData}
                            onClose={() => {
                                setIsAddUpdateModalOpen(false);
                                setSelectedEmission(null);
                            }}
                            emissionId={selectedEmission?.id}
                            initialData={selectedEmission}
                        />
                    </DialogContent>
                </Dialog>

                <Dialog
                    open={deleteDialogOpen}
                    onClose={() => setDeleteDialogOpen(false)}
                >
                    <DialogTitle>Confirm Delete</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to delete this vehicle emission record? This action cannot be undone.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <div className="flex justify-end gap-5 items-center mt-5">
                            <CustomButton
                                className="bg-gray-500 hover:bg-gray-700 text-white"
                                text="Cancel"
                                disabled={isSubmitting}
                                click={() => setDeleteDialogOpen(false)}
                            />
                            <CustomButton
                                className="bg-red-600 hover:bg-red-700 text-white"
                                click={confirmDelete}
                                text="Delete"
                                disabled={isSubmitting}
                            />
                        </div>
                    </DialogActions>
                </Dialog>
            </PageHeader>
        </>
    )
}

export default EmissionVehicleDistanceWise